/**
 * v109.9.14 — Travel · Account Team tab
 * -------------------------------------
 * Restricted to Master Admin / Accounts Admin.
 * Sub-tabs by lifecycle stage:
 *   • Pending Request   — All prior approvals done, awaiting Account Team.
 *   • Approved Request  — Account Team has approved (awaiting disbursement).
 *   • Dispersed Request — Marked Disbursed (with optional Mark Ledger lock).
 *
 * Inside each sub-tab: filter chips → All | Advance | Travel Settlement.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { moduleCanDo } from "../lib/modules";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plane, ReceiptText, Wallet, Users, ShieldCheck, Clock, CheckCircle2, Banknote, Eye, Pencil } from "lucide-react";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import TravelAdvanceActions from "../components/TravelAdvanceActions";
import TravelSettlementFormDialog from "../components/TravelSettlementFormDialog";
import { emitDataChange, useDataChange } from "../lib/events";
import { progressiveStatus } from "./TravelAdvancesTab";

const BUCKETS = [
  { key: "pending",   label: "Pending Request",   icon: Clock },
  { key: "approved",  label: "Approved Request",  icon: CheckCircle2 },
  { key: "disbursed", label: "Dispersed Request", icon: Banknote },
];
const KIND_FILTERS = [
  { key: "all",        label: "All" },
  { key: "advance",    label: "Advance" },
  { key: "settlement", label: "Travel Settlement" },
];

function money(v) { return `₹ ${Number(v || 0).toLocaleString("en-IN")}`; }

export default function TravelAccountsQueueTab() {
  const { user } = useAuth();
  const [viewMode] = useGlobalViewMode();
  const [bucket, setBucket] = useState("pending");
  const [kind, setKind] = useState("all");
  const [busy, setBusy] = useState(false);
  const [queue, setQueue] = useState({ advances: [], settlements: [] });
  const [allUnsettled, setAllUnsettled] = useState([]);
  // v109.9.174 — Accounts-side edit + open (view details) for settlements.
  const [stlEdit, setStlEdit] = useState(null);
  const [stlView, setStlView] = useState(null);

  const isAdmin = user?.role === "master_admin" || moduleCanDo(user, "travel", "edit");

  const load = async () => {
    if (!isAdmin) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/travel/accounts-queue?status_bucket=${bucket}&kind=${kind}`);
      setQueue(data || { advances: [], settlements: [] });
      const { data: uns } = await api.get("/travel-advances/unsettled-summary?scope=all").catch(() => ({ data: [] }));
      setAllUnsettled(uns || []);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [bucket, kind, isAdmin]);

  // v109.9.179 — Refresh queue when any Travel record is mutated elsewhere.
  useDataChange(["travel:updated"], () => load());

  const totals = useMemo(() => ({
    advances: queue.advances.length,
    settlements: queue.settlements.length,
    unsettledSum: allUnsettled.reduce((s, r) => s + (r.unsettled || 0), 0),
    overdueEmp: allUnsettled.filter((r) => r.overdue_count > 0 && r.unsettled > 0).length,
  }), [queue, allUnsettled]);

  if (!isAdmin) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/40 p-10 text-center text-slate-500">
        <ShieldCheck className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <div className="text-[13px] font-semibold">Account team access required</div>
        <div className="text-[11.5px] mt-1">This tab is restricted to Master Admin and Accounts Admin users.</div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="travel-accounts-queue">
      {/* Top summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <SummaryCard tone="amber"   label={`${BUCKETS.find(b=>b.key===bucket)?.label} · Advances`}    value={totals.advances}    icon={<Plane size={13} />} />
        <SummaryCard tone="violet"  label={`${BUCKETS.find(b=>b.key===bucket)?.label} · Settlements`} value={totals.settlements} icon={<ReceiptText size={13} />} />
        <SummaryCard tone="emerald" label="Unsettled Total"    value={money(totals.unsettledSum)} icon={<Wallet size={13} />} />
        <SummaryCard tone="rose"    label="Overdue Employees"  value={totals.overdueEmp}         icon={<Users size={13} />} />
      </div>

      {/* Bucket sub-tabs */}
      <Tabs value={bucket} onValueChange={setBucket} className="w-full">
        <TabsList className="flex flex-wrap gap-1.5 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 p-1.5 rounded-lg">
          {BUCKETS.map((b) => {
            const Icon = b.icon;
            return (
              <TabsTrigger
                key={b.key}
                value={b.key}
                className="data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900 data-[state=active]:shadow-sm text-[12.5px] px-3 py-1.5 flex items-center gap-1.5"
                data-testid={`acc-bucket-${b.key}`}
              >
                <Icon size={13} /> {b.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {BUCKETS.map((b) => (
          <TabsContent key={b.key} value={b.key} className="mt-4 space-y-4">
            {/* Kind filter chips */}
            <div className="flex flex-wrap gap-1.5" data-testid="acc-kind-filters">
              {KIND_FILTERS.map((k) => (
                <button
                  key={k.key}
                  onClick={() => setKind(k.key)}
                  className={`text-[11.5px] px-3 py-1.5 rounded-full border transition-all ${
                    kind === k.key
                      ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                      : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:border-indigo-400"
                  }`}
                  data-testid={`acc-kind-${k.key}`}
                >
                  {k.label}
                </button>
              ))}
              <Button size="sm" variant="outline" onClick={load} className="ml-auto h-8 text-[11.5px]" data-testid="acc-refresh">
                {busy ? "…" : "Refresh"}
              </Button>
            </div>

            {(kind === "all" || kind === "advance") && (
              <Section title="Travel Advances" count={totals.advances} icon={<Plane size={14} />}>
                {queue.advances.length === 0 ? <Empty label="No advances in this bucket." /> : viewMode === "card" ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-3" data-testid="acc-adv-cards">
                    {queue.advances.map((r) => {
                      const { label, tone } = progressiveStatus(r);
                      return (
                        <div key={r.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`acc-adv-card-${r.id}`}>
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="min-w-0 flex-1">
                              <div className="font-bold text-slate-900 dark:text-white truncate">{r.employee_name || r.created_by_name}</div>
                              <div className="text-[10.5px] text-slate-500 truncate">{r.employee_code || ""}</div>
                            </div>
                            <Badge className={`${tone} text-[10px] shrink-0`}>{label}</Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                            <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Purpose</div><div className="text-slate-800 truncate">{r.travel_purpose}</div></div>
                            <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">From</div><div className="text-slate-700 text-[10.5px]">{(r.from_datetime || r.from_date || "").replace("T"," ").slice(0,16)}</div></div>
                            <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Amount</div><div className="mono font-semibold">{money(r.advance_amount)}</div></div>
                            <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Flow</div><div><Badge variant="outline" className="text-[10px]">{r.approval_type || "—"}</Badge></div></div>
                          </div>
                          <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                            <TravelAdvanceActions row={r} onChanged={load} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <Table cols={["Employee", "Purpose", "From", "Amount", "Flow", "Status", "Actions"]}>
                    {queue.advances.map((r) => {
                      const { label, tone } = progressiveStatus(r);
                      return (
                        <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`acc-adv-row-${r.id}`}>
                          <td className="p-2.5">
                            <div className="font-semibold">{r.employee_name || r.created_by_name}</div>
                            <div className="text-[10.5px] text-slate-500">{r.employee_code || ""}</div>
                          </td>
                          <td className="p-2.5">{r.travel_purpose}</td>
                          <td className="p-2.5 text-[11px]">{(r.from_datetime || r.from_date || "").replace("T"," ").slice(0,16)}</td>
                          <td className="p-2.5 text-right font-mono font-semibold">{money(r.advance_amount)}</td>
                          <td className="p-2.5"><Badge variant="outline" className="text-[10px]">{r.approval_type || "—"}</Badge></td>
                          <td className="p-2.5"><Badge className={`${tone} text-[10px]`}>{label}</Badge></td>
                          <td className="p-2.5 text-right"><TravelAdvanceActions row={r} onChanged={load} /></td>
                        </tr>
                      );
                    })}
                  </Table>
                )}
              </Section>
            )}

            {(kind === "all" || kind === "settlement") && (
              <Section title="Travel Settlements" count={totals.settlements} icon={<ReceiptText size={14} />}>
                {queue.settlements.length === 0 ? <Empty label="No settlements in this bucket." /> : viewMode === "card" ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-3" data-testid="acc-clm-cards">
                    {queue.settlements.map((r) => (
                      <div key={r.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`acc-clm-card-${r.id}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="min-w-0 flex-1">
                            <div className="font-bold text-slate-900 dark:text-white truncate">{r.employee_name || r.created_by_name}</div>
                            <div className="text-[10.5px] text-slate-500 truncate">{r.employee_code || ""}</div>
                          </div>
                          <Badge className="text-[10px] shrink-0">{r.status}</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                          <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Amount</div><div className="mono font-semibold">{money(r.claim_amount)}</div></div>
                          <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Approver</div><div className="text-slate-800 truncate">{r.approver_name || "—"}</div></div>
                          <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Email</div><div className="text-slate-700 truncate">{r.email_submitted || "—"}</div></div>
                        </div>
                        <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                          <SettlementAccountsActions row={r} onChanged={load} onEdit={setStlEdit} onView={setStlView} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Table cols={["Employee", "Amount", "Approver", "Email", "Status", "Actions"]}>
                    {queue.settlements.map((r) => (
                      <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`acc-clm-row-${r.id}`}>
                        <td className="p-2.5">
                          <div className="font-semibold">{r.employee_name || r.created_by_name}</div>
                          <div className="text-[10.5px] text-slate-500">{r.employee_code || ""}</div>
                        </td>
                        <td className="p-2.5 text-right font-mono">{money(r.claim_amount)}</td>
                        <td className="p-2.5">{r.approver_name || "—"}</td>
                        <td className="p-2.5">{r.email_submitted || "—"}</td>
                        <td className="p-2.5"><Badge className="text-[10px]">{r.status}</Badge></td>
                        <td className="p-2.5 text-right"><SettlementAccountsActions row={r} onChanged={load} onEdit={setStlEdit} onView={setStlView} /></td>
                      </tr>
                    ))}
                  </Table>
                )}
              </Section>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* v109.9.174 — Accounts-side Settlement edit (audit fields locked
          by the form dialog itself) and read-only Open-details banner. */}
      {stlEdit && (
        <TravelSettlementFormDialog
          open
          onClose={() => setStlEdit(null)}
          onSaved={() => { setStlEdit(null); load(); }}
          editRecord={stlEdit}
        />
      )}
      {stlView && (
        <SettlementViewDialog row={stlView} onClose={() => setStlView(null)} />
      )}
    </div>
  );
}

// v109.9.174 — Read-only "Open" view for settlements in the Accounts Tab.
// Shows every field filled by the employee + the current approval chain.
function SettlementViewDialog({ row, onClose }) {
  const isLocal = row.kind === "local";
  const money = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto" data-testid={`stl-view-${row.id}`}>
        <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 px-5 py-3 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-bold text-slate-900 dark:text-white">
              Settlement · {row.created_by_name || "—"}
              {row.settlement_code ? <span className="ml-2 mono text-indigo-700 text-[12px]">{row.settlement_code}</span> : null}
            </div>
            <div className="text-[11.5px] text-slate-500">
              {isLocal ? `Local Travel · ${row.month || ""} ${row.year || ""}`.trim() : `Tour · ${row.purpose_of_travel || "—"}`}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800 text-[13px]" data-testid={`stl-view-close-${row.id}`}>Close ✕</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
            <KV k="Employee" v={row.created_by_name} />
            <KV k="Employee Code" v={row.employee_code} />
            <KV k="Reporting Manager" v={row.reporting_manager_name} />
            {row.assigned_by_name ? <KV k="Assigned By" v={row.assigned_by_name} /> : null}
            <KV k="Kind" v={row.kind} />
            <KV k="Claim Amount" v={money(row.claim_amount)} />
            <KV k="Status" v={row.status} />
            <KV k="Sender Email" v={row.sender_email} />
            {row.client_names?.length ? <KV k="Clients" v={row.client_names.join(", ")} full /> : null}
          </div>

          {/* Local */}
          {isLocal && (row.local_entries || []).length > 0 && (
            <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2.5">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Local Entries ({row.local_entries.length})</div>
              <div className="space-y-1">
                {row.local_entries.map((e, i) => (
                  <div key={i} className="text-[11.5px] border-b border-slate-100 dark:border-slate-800 py-1.5 last:border-b-0">
                    <div className="font-semibold">{e.date} · {e.start_location} → {e.end_location}</div>
                    {e.reason && <div className="text-[10.5px] text-slate-500">Reason: {e.reason}</div>}
                    {(e.conveyances || []).map((c, ci) => (
                      <div key={ci} className="pl-3 text-[10.5px] text-slate-700 dark:text-slate-300 flex justify-between">
                        <span>{c.mode}{c.mode_other ? ` (${c.mode_other})` : ""}{c.kms ? ` · ${c.kms} km` : ""}</span>
                        <span className="mono">{money(c.amount)}</span>
                      </div>
                    ))}
                    <div className="text-right text-[10.5px] mt-0.5">Day total: <b>{money(e.day_total)}</b></div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {isLocal && (row.local_misc_items || []).length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50/40 p-2.5">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-800 mb-1">Miscellaneous ({row.local_misc_items.length})</div>
              {row.local_misc_items.map((m, i) => (
                <div key={i} className="flex justify-between text-[11.5px]">
                  <span>{m.date} · {m.expense_type} — {m.reason}</span>
                  <span className="mono font-semibold">{money(m.amount)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Tour sections */}
          {!isLocal && row.section_totals && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[11.5px]">
              {["s1", "s2", "s3", "s4", "s5"].map((k) => (
                <div key={k} className="rounded border border-slate-200 dark:border-slate-700 px-2 py-1">
                  <div className="text-slate-500 text-[10px] uppercase tracking-wider">{k.toUpperCase()}</div>
                  <div className="mono font-semibold">{money(row.section_totals?.[k])}</div>
                </div>
              ))}
            </div>
          )}
          {!isLocal && (row.s1_lines || []).length > 0 && (
            <SectionList title="S1 · Inter-State" items={row.s1_lines}
              render={(l) => `${l.mode} · ${l.travel_class || "—"} · ${l.departure_location} → ${l.arrival_location} · ${money(l.amount)}`} />
          )}
          {!isLocal && (row.s2_entries || []).length > 0 && (
            <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2.5">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">S2 · Local Conveyance</div>
              {row.s2_entries.map((e, i) => (
                <div key={i} className="text-[11.5px] border-b border-slate-100 py-1 last:border-b-0">
                  <div className="font-semibold">{e.date} · {e.start_location} → {e.end_location}</div>
                  {(e.conveyances || []).map((c, ci) => (
                    <div key={ci} className="pl-3 text-[10.5px] flex justify-between">
                      <span>{c.mode}{c.kms ? ` · ${c.kms} km` : ""}</span>
                      <span className="mono">{money(c.amount)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          {!isLocal && row.s3?.days > 0 && (
            <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2.5">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">S3 · DA / Fooding</div>
              <div className="text-[11.5px]">
                Days: <b>{row.s3.days}</b> · Rate: <b>{money(row.s3.accounts_per_day_amount || row.s3.per_day_amount)}</b>
              </div>
            </div>
          )}
          {!isLocal && (row.s4_hotels || []).length > 0 && (
            <SectionList title="S4 · Lodging" items={row.s4_hotels}
              render={(h) => `${h.hotel_name || "Hotel"} · ${h.location || ""} · ${h.from_date}→${h.to_date} · ${h.days || 0}d · ${money(h.total || ((h.days || 0) * (h.amount_per_day || 0)))}`} />
          )}
          {!isLocal && (row.s5_items || []).length > 0 && (
            <SectionList title="S5 · Miscellaneous" items={row.s5_items}
              render={(m) => `${m.date} · ${m.expense_type} — ${m.reason} · ${money(m.amount)}`} />
          )}

          {/* Approval chain */}
          {(row.approvals || []).length > 0 && (
            <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2.5">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Approval Chain</div>
              <div className="space-y-1.5">
                {row.approvals.map((a, i) => (
                  <div key={i} className={`flex items-center justify-between rounded-md border px-2 py-1.5 text-[11.5px] ${
                    a.status === "approved" ? "border-emerald-200 bg-emerald-50/60" :
                    a.status === "rejected" ? "border-rose-200 bg-rose-50/60" :
                    "border-amber-200 bg-amber-50/60"
                  }`}>
                    <div className="font-semibold">
                      {a.stage === "assigned_by" ? "Assigned By" : a.stage === "reporting_manager" ? "Reporting Manager" : "Accounts"}
                      <span className="ml-1.5 text-slate-500 font-normal">· {a.name || "—"}</span>
                    </div>
                    <Badge className={`text-[10px] ${
                      a.status === "approved" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                      a.status === "rejected" ? "bg-rose-100 text-rose-700 border-rose-200" :
                      "bg-amber-100 text-amber-800 border-amber-200"
                    }`}>{a.status}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KV({ k, v, full }) {
  return (
    <div className={full ? "md:col-span-4" : ""}>
      <div className="text-[9.5px] uppercase tracking-wider font-semibold text-slate-500">{k}</div>
      <div className="text-[12px] text-slate-800 dark:text-slate-200 mt-0.5 whitespace-pre-wrap break-words">{v || "—"}</div>
    </div>
  );
}

function SectionList({ title, items, render }) {
  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-700 p-2.5">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">{title}</div>
      <div className="space-y-1 text-[11.5px]">
        {items.map((it, i) => <div key={i} className="border-b border-slate-100 last:border-b-0 py-1">{render(it)}</div>)}
      </div>
    </div>
  );
}

function Section({ title, count, icon, children }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60">
        <div className="text-[12.5px] font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
          {icon} {title}
        </div>
        <Badge variant="outline" className="text-[10.5px]">{count}</Badge>
      </div>
      {children}
    </div>
  );
}

function Table({ cols, children }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12.5px]">
        <thead className="bg-slate-50 dark:bg-slate-800/40 text-[10.5px] uppercase tracking-wider text-slate-600 dark:text-slate-300">
          <tr>{cols.map((c) => <th key={c} className="p-2.5 text-left whitespace-nowrap">{c}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Empty({ label }) {
  return <div className="p-6 text-center text-[12px] text-slate-500">{label}</div>;
}

function SummaryCard({ tone, label, value, icon }) {
  const toneCls = {
    amber:   "border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-200",
    violet:  "border-violet-300 bg-violet-50 text-violet-900 dark:bg-violet-950/40 dark:border-violet-800 dark:text-violet-200",
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-200",
    rose:    "border-rose-300 bg-rose-50 text-rose-900 dark:bg-rose-950/40 dark:border-rose-800 dark:text-rose-200",
  }[tone];
  return (
    <div className={`rounded-lg border p-2.5 ${toneCls}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5 opacity-80">
        {icon} {label}
      </div>
      <div className="text-[16px] font-bold mt-0.5">{value}</div>
    </div>
  );
}

// v109.9.171 — Settlement action buttons on the Account Teams queue.
// - Pending  → Open · Edit · Approve · Reject
// - Approved → Open · Edit · Mark Disbursed (single click)
// - Downloaded (Disbursed bucket) → Mark Ledger (locks the row)
// - Ledgered → shows "Ledgered · Locked" badge
function SettlementAccountsActions({ row, onChanged, onEdit, onView }) {
  const [busy, setBusy] = useState(false);
  const doPost = async (path, payload = null, ok = "Done", confirmMsg = null) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      await api.post(`/travel-settlements/${row.id}${path}`, payload || {});
      toast.success(ok);
      // v109.9.179 — Broadcast so dashboard tiles / other tabs refresh.
      emitDataChange("travel:updated", { kind: "settlement", id: row.id, action: path });
      if (path === "/mark-disbursed") emitDataChange("travel:disbursed", { id: row.id, kind: "settlement" });
      if (path === "/mark-ledger")    emitDataChange("travel:ledgered",  { id: row.id, kind: "settlement" });
      onChanged && onChanged();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const status = row.status || "";
  const ledgered = status === "Ledgered" || !!row.ledgered_at;
  return (
    <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
      {onView && (
        <button
          onClick={() => onView(row)}
          className="h-7 px-2 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 text-[11px] font-semibold inline-flex items-center gap-1"
          data-testid={`stl-open-${row.id}`}
        ><Eye size={11} /> Open</button>
      )}
      {onEdit && !ledgered && (
        <button
          onClick={() => onEdit(row)}
          className="h-7 px-2 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 text-[11px] font-semibold inline-flex items-center gap-1"
          data-testid={`stl-edit-${row.id}`}
        ><Pencil size={11} /> Edit</button>
      )}
      {(status === "Submitted" || status === "ManagerApproved") && (
        <>
          <button
            onClick={() => doPost("/step-approve", { remark: "" }, "Approved", "Approve this settlement?")}
            disabled={busy}
            className="h-7 px-2 rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-[11px] font-semibold"
            data-testid={`stl-approve-${row.id}`}
          >Approve</button>
          <button
            onClick={() => {
              const r = window.prompt("Rejection reason:", "");
              if (!r || !r.trim()) return;
              doPost("/step-reject", { remark: r.trim() }, "Rejected");
            }}
            disabled={busy}
            className="h-7 px-2 rounded-md border border-rose-200 text-rose-700 hover:bg-rose-50 text-[11px] font-semibold"
            data-testid={`stl-reject-${row.id}`}
          >Reject</button>
        </>
      )}
      {status === "Approved" && (
        <button
          onClick={() => doPost("/mark-disbursed", {}, "Marked Disbursed", "Mark this settlement as Disbursed? It will move to the Dispersed Request bucket.")}
          disabled={busy}
          className="h-7 px-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-semibold"
          data-testid={`stl-disburse-${row.id}`}
        >Mark Disbursed</button>
      )}
      {status === "Downloaded" && !row.ledgered_at && (
        <button
          onClick={() => doPost("/mark-ledger", { remark: "" }, "Marked Ledgered — record locked", "Mark this settlement as Ledgered? Once ledgered it is LOCKED for everyone and cannot be edited further.")}
          disabled={busy}
          className="h-7 px-2 rounded-md bg-slate-700 hover:bg-slate-800 text-white text-[11px] font-semibold"
          data-testid={`stl-ledger-${row.id}`}
        >Mark Ledger</button>
      )}
      {ledgered && (
        <Badge className="text-[10px] bg-slate-800 text-white border-slate-800" data-keep-color data-testid={`stl-ledgered-${row.id}`}>
          Ledgered · Locked
        </Badge>
      )}
    </div>
  );
}
